import classNames from 'classnames';
import { ReactNode } from 'react';

import { setupForDark } from '@theme';

interface ButtonProps extends React.HTMLAttributes<HTMLElement> {
  children: ReactNode;
  onClick: any;
  type?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'quaternary'
    | 'additional'
    | 'confirm'
    | 'dashed'
    | 'textual';
  size?: 'large' | 'medium' | 'small' | 'modal';
  disabled?: boolean;
  fullWidth?: boolean;
  border?: boolean;
  extraClassName?: string;
  labelFor?: string;
  fitContent?: boolean;
}

export default function Button({
  children,
  onClick,
  type = 'primary',
  size = 'medium',
  disabled = false,
  fullWidth = false,
  border = true,
  extraClassName = '',
  labelFor = '',
  fitContent = false,
  className,
  ...props
}: ButtonProps) {
  // secondary buttons have a border
  let extraBorderClasses = '';
  if (type === 'secondary') {
    switch (size) {
      case 'large':
        extraBorderClasses = 'border-[1.5px]';
        break;

      case 'medium':
        extraBorderClasses = 'border-[1.5px]';
        break;

      case 'small':
        extraBorderClasses = 'border-[1.2px]';
        break;
    }
  }

  if (!border) {
    extraBorderClasses = '';
  }

  let typeClasses = '';
  if (!disabled) {
    switch (type) {
      case 'primary':
        typeClasses = classNames(
          setupForDark(
            'bg-button1 text-button1 hover:bg-button1-hover hover:text-button1-hover hover:delay-100'
          )
        );
        break;

      case 'secondary':
        typeClasses = classNames(
          setupForDark(
            'text-button2 border-button2 hover:text-button2-hover hover:border-button2-hover hover:delay-100',
            extraBorderClasses
          )
        );
        break;

      case 'tertiary':
        typeClasses = classNames(
          setupForDark(
            'bg-button3 text-button3 hover:bg-button3-hover hover:text-button3-hover hover:delay-100'
          )
        );
        break;

      case 'quaternary':
        typeClasses = classNames(
          setupForDark(
            'bg-button4 text-white hover:bg-button4-hover hover:text-button4-hover hover:delay-100'
          )
        );
        break;

      case 'additional':
        typeClasses = classNames(
          setupForDark(
            'bg-violet-700 text-button1 hover:bg-violet-500 hover:text-button1 hover:delay-100'
          )
        );
        break;

      case 'confirm':
        typeClasses = classNames(
          setupForDark('bg-button-violet text-button1 hover:bg-button-violet-hover hover:delay-100')
        );
        break;

      case 'dashed':
        typeClasses = classNames(
          setupForDark('text-secondary border-tertiary'),
          'border-dashed border-[1px]'
        );
        break;

      case 'textual':
        typeClasses = classNames(setupForDark('text-button2 hover:text-button2-hover'));
        break;
    }
  } else {
    switch (type) {
      case 'primary':
        typeClasses = classNames(setupForDark('bg-button1-disabled text-button1-disabled'));
        break;

      case 'secondary':
        typeClasses = classNames(
          setupForDark('text-button2-disabled border-button2-disabled', extraBorderClasses)
        );
        break;

      case 'tertiary':
        typeClasses = classNames(setupForDark('bg-button3-disabled text-button3-disabled'));
        break;

      case 'quaternary':
        typeClasses = classNames(setupForDark('bg-button4-disabled text-button4-disabled'));
        break;

      case 'additional':
        typeClasses = classNames(setupForDark('bg-violet-300 text-button1-disabled'));
        break;

      case 'confirm':
        typeClasses = classNames(setupForDark('bg-button-violet-disabled text-button1-disabled'));
        break;

      case 'dashed':
        typeClasses = classNames(
          setupForDark('border-secondary text-button-dashed-disabled'),
          'border-dashed border-[1px]'
        );
        break;

      case 'textual':
        typeClasses = classNames(setupForDark('text-button2-disabled border-none'));
        break;
    }
  }

  let sizeClasses = '';
  switch (size) {
    case 'large':
      sizeClasses = classNames('pt-sm pb-sm pl-md pr-md rounded-xs', 'P1B');
      break;

    case 'medium':
      sizeClasses = classNames('pt-xs pb-xs pl-sm pr-sm rounded-xs', 'P2M');
      break;

    case 'small':
      sizeClasses = classNames('py-xs px-sm rounded-xs', 'P4B');
      if (type === 'textual') {
        sizeClasses = classNames('rounded-xs', 'P4B');
      }
      break;

    case 'modal':
      sizeClasses = classNames('h-[44px] px-md rounded-xs uppercase', 'P3M');
      break;
  }

  onClick = disabled
    ? (event: React.SyntheticEvent) => {
        event.stopPropagation();
        event.preventDefault();
      }
    : onClick;
  const composedClassName = classNames(
    'flex flex-row items-center justify-center gap-xs',
    disabled ? '' : 'cursor-pointer',
    typeClasses,
    sizeClasses,
    fitContent ? 'w-fit' : '',
    fullWidth ? 'w-full' : '',
    !fullWidth ? 'dk:max-w-[370px]' : '',
    extraClassName,
    className
  );

  return (
    <>
      {labelFor && (
        <label className={composedClassName} onClick={onClick} htmlFor={labelFor} {...props}>
          {children}
        </label>
      )}
      {!labelFor && (
        <div className={composedClassName} onClick={onClick} {...props}>
          {children}
        </div>
      )}
    </>
  );
}
